import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { PageContainer } from "@/components/layout/page-container";
import { PageHeader } from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  CATEGORY_META,
  MediaFlags,
  ResultSkeleton,
  SEARCH_PER_GROUP,
  TagBadge,
  formatDate,
  inspectionTitle,
  type SearchCategory,
  type SearchGroup,
} from "@/components/global-search/search-shared";
import { useSearchQuery } from "../../services/api";

const CATEGORIES: SearchCategory[] = ["inspection", "resource", "item"];
const MEDIA_TYPES = ["image", "video", "sound"] as const;

/** Full search results page (`/dashboard/search?q=…`) with category + media filters. */
export default function SearchPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const q = params.get("q") ?? "";

  const [categories, setCategories] = React.useState<SearchCategory[]>([]);
  const [mediaTypes, setMediaTypes] = React.useState<string[]>([]);

  // Omit empty filters so that — with no filters active — this arg is identical
  // to the dropdown's ({ q, perGroup }). RTK Query then serves both from one
  // cache entry, so arriving here via Enter reuses the dropdown's request
  // instead of firing a second, identical search.
  const { data, isFetching } = useSearchQuery(
    {
      q,
      perGroup: SEARCH_PER_GROUP,
      lang: i18n.language,
      ...(categories.length ? { categories } : {}),
      ...(mediaTypes.length ? { mediaTypes } : {}),
    },
    { skip: q.trim().length < 2 },
  );
  const groups: SearchGroup[] = data?.groups ?? [];
  const hasResults = groups.some((g) => g.results.length > 0);
  const totalResults = groups.reduce((sum, g) => sum + g.total, 0);

  const toggle = <T,>(list: T[], value: T, set: (v: T[]) => void) =>
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  return (
    <PageContainer title={t("search.page-title")}>
      <PageHeader
        title={t("search.page-title")}
        description={q ? t("search.results-for", { query: q }) : t("search.page-subtitle")}
      />

      {/* Filters */}
      <div className="mt-4 mb-6 flex flex-wrap items-center gap-2">
        {CATEGORIES.map((c) => (
          <Button
            key={c}
            size="sm"
            variant={categories.includes(c) ? "default" : "outline"}
            onClick={() => toggle(categories, c, setCategories)}
          >
            {t(CATEGORY_META[c].labelKey)}
          </Button>
        ))}
        <span className="mx-1 h-5 w-px bg-border" />
        {MEDIA_TYPES.map((m) => (
          <Button
            key={m}
            size="sm"
            variant={mediaTypes.includes(m) ? "default" : "outline"}
            onClick={() => toggle(mediaTypes, m, setMediaTypes)}
          >
            {t(`search.media.${m}`)}
          </Button>
        ))}
      </div>

      {isFetching && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="gap-2 p-4">
              <ResultSkeleton />
            </Card>
          ))}
        </div>
      )}

      {!isFetching && q.trim().length >= 2 && !hasResults && (
        <p className="text-sm text-muted-foreground">{t("search.no-results")}</p>
      )}

      {!isFetching && hasResults && (
        <p className="mb-4 text-sm text-muted-foreground">
          {t("search.result-count", { count: totalResults })}
        </p>
      )}

      {!isFetching && (
        <div className="space-y-8">
          {groups.map((group) => (
            <section key={group.category}>
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                {t(CATEGORY_META[group.category].labelKey)}
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{group.total}</span>
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {group.results.map((r) => (
                  <Card
                    key={`${group.category}-${r.id}`}
                    className="cursor-pointer gap-2 p-4 transition-colors hover:bg-accent"
                    onClick={() => navigate(CATEGORY_META[group.category].route(r.id))}
                  >
                    <div className="flex w-full items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium">
                          {group.category === "inspection" ? inspectionTitle(r) : r.title}
                        </div>
                        {group.category === "inspection" && (r.title || r.date) && (
                          <div className="text-xs text-muted-foreground">
                            {[r.title && `#${r.title}`, formatDate(r.date)]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        )}
                      </div>
                      <MediaFlags media={r.media} />
                    </div>
                    {r.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {r.tags.map((tag) => (
                          <TagBadge key={tag.id} tag={tag} />
                        ))}
                      </div>
                    )}
                    {r.matchedItems && r.matchedItems.length > 0 && (
                      <ul className="mt-1 space-y-1 border-t pt-2 text-xs">
                        {r.matchedItems.map((mi) => (
                          <li key={mi.itemId} className="px-1">
                            <div className="font-medium">{mi.title}</div>
                            {mi.description && (
                              <p className="mt-0.5 line-clamp-3 whitespace-pre-line text-muted-foreground">
                                {mi.description}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
